import { useCallback, useEffect, useRef, useState } from 'react'
import firebase from '../../firebase/compat'

/**
 * Haalt de gesprekken van álle organisaties met Decca op, pagina voor pagina.
 *
 * Loopt via een Cloud Function en niet via de Firestore-hooks, omdat
 * `Conversations` in de regels `isTeam()`-gated is: de beheertenant mag de
 * gesprekken van andere organisaties niet in de browser lezen. Een geweigerde
 * read geeft daar een lege lijst en geen fout, dus de pagina zou stil beweren
 * dat er geen gesprekken zijn.
 *
 * `rows` is `null` zolang er geen antwoord is. Dat is iets anders dan een lege
 * lijst: zonder dat onderscheid beweert het scherm tijdens het laden en na een
 * fout hetzelfde als bij een platform zonder gesprekken.
 * AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
 *
 * @param {number} pageSize - aantal gesprekken per ronde
 * @param {boolean} [enabled] - alleen ophalen wanneer de lijst ook getoond wordt
 * @returns {{rows: Array<object> | null, loading: boolean, loadingMore: boolean, error: string | null, hasMore: boolean, loadMore: () => void}}
 */
const useDeccaConversations = (pageSize, enabled = true) => {

    const [rows, setRows] = useState(/** @type {Array<object> | null} */ (null))
    const [loading, setLoading] = useState(Boolean(enabled))
    const [loadingMore, setLoadingMore] = useState(false)
    const [error, setError] = useState(/** @type {string | null} */ (null))
    const [cursor, setCursor] = useState(/** @type {object | null} */ (null))
    const [hasMore, setHasMore] = useState(false)

    // Refs en geen state: `loadMore` mag niet van identiteit wisselen zodra er
    // een ronde loopt, anders vuurt het effect dat aan het einde van de lijst
    // hangt opnieuw en stapelen de rondes zich op.
    const bezig = useRef(false)
    const afgebroken = useRef(false)

    useEffect(() => {
        afgebroken.current = false
        return () => { afgebroken.current = true }
    }, [])

    /**
     * @param {object | null} vanafCursor - null voor de eerste pagina
     * @returns {Promise<void>}
     */
    const fetchPage = useCallback(async (vanafCursor) => {

        if (bezig.current) return
        bezig.current = true
        if (vanafCursor) setLoadingMore(true); else setLoading(true)
        setError(null)

        try {
            // v2-functie in europe-west1 — regio expliciet, anders belt de SDK
            // us-central1 en bestaat de functie daar niet.
            const callable = firebase.app().functions('europe-west1').httpsCallable('getDeccaConversations')
            const result = await callable({ limit: pageSize, cursor: vanafCursor })
            if (afgebroken.current) return

            const nieuwe = Array.isArray(result.data?.rows) ? result.data.rows : []
            setRows((vorige) => (vanafCursor && vorige ? [...vorige, ...nieuwe] : nieuwe))
            setCursor(result.data?.nextCursor || null)
            setHasMore(Boolean(result.data?.hasMore))
        } catch (err) {
            if (afgebroken.current) return
            // eslint-disable-next-line no-console
            console.error('useDeccaConversations: getDeccaConversations failed', err)
            // De ruwe melding erbij, maar niet in plaats van een leesbare zin:
            // een mislukte callable levert vaak alleen het codewoord 'internal'
            // op, en dat zegt de lezer niets.
            setError(err.message
                ? `Kon de gesprekken niet ophalen (${err.message}).`
                : 'Kon de gesprekken niet ophalen.')
            // Bij een fout op de eerste ronde blijft `rows` null, zodat het
            // scherm de fout toont en niet 'geen gesprekken'. Bij een fout op
            // een vervolgronde blijft staan wat er al is.
            //
            // En dan blijft `hasMore` ook staan: de cursor is nog geldig, dus
            // 'Meer laden' kan het gewoon opnieuw proberen. Hem op false zetten
            // kapt de lijst definitief af bij iets tijdelijks — gezien in
            // productie toen het inlogtoken na een uur verliep en Cloud Run
            // één ronde met 401 weigerde.
            if (!vanafCursor) setHasMore(false)
        } finally {
            if (!afgebroken.current) {
                setLoading(false)
                setLoadingMore(false)
            }
            bezig.current = false
        }
    }, [pageSize])

    useEffect(() => {
        if (!enabled) {
            setLoading(false)
            setError(null)
            return
        }
        fetchPage(null)
    }, [enabled, fetchPage])

    const loadMore = useCallback(() => {
        if (!hasMore || bezig.current || !cursor) return
        fetchPage(cursor)
    }, [hasMore, cursor, fetchPage])

    return { rows, loading, loadingMore, error, hasMore, loadMore }
}

export default useDeccaConversations
